import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Box, Typography, Chip, CircularProgress } from "@mui/material";
import { Prisma } from "@prisma/client";
import { getPost } from "src/utils/apicalls";
import { trackEvent } from "src/utils/analytics";
import SeoHead from "../../Components/SeoHead";
import { useLocale } from "src/i18n/LocaleProvider";
import { DEFAULT_LOCALE } from "src/i18n/localesConfig";
import {
  blogPostingSchema,
  breadcrumbSchema,
  extractFaqsFromHtml,
  faqPageSchema,
  organizationSchema,
} from "src/utils/seoSchema";

type PostWithAuthor = Prisma.PostGetPayload<{
  include: {
    author: true; // Include the related author
  };
}>;

const hideBrokenImage = (img: HTMLImageElement) => {
  img.style.display = "none";
  img.removeAttribute("src");
  img.alt = "";
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { locale } = useLocale();
  const [post, setPost] = useState<PostWithAuthor | null>(null);
  const [loading, setLoading] = useState(true);
  const [coverFailed, setCoverFailed] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const script1 = document.createElement("script");
    script1.src = "https://news.google.com/swg/js/v1/swg-basic.js";
    script1.async = true;

    const script2 = document.createElement("script");
    script2.type = "application/javascript";
    script2.innerHTML = `
      (self.SWG_BASIC = self.SWG_BASIC || []).push( basicSubscriptions => {
        basicSubscriptions.init({
          type: "NewsArticle",
          isPartOfType: ["Product"],
          isPartOfProductId: "CAowpeTZCw:openaccess",
          clientOptions: { theme: "light", lang: "en" },
        });
      });
    `;

    document.body.appendChild(script1);
    document.body.appendChild(script2);

    return () => {
      document.body.removeChild(script1);
      document.body.removeChild(script2);
    };
  }, []);

  useEffect(() => {
    if (slug) {
      setCoverFailed(false);
      getPost(
        slug,
        (response) => {
          setPost(response); // Set the post data
          setLoading(false);
          trackEvent("blog_post_view", { slug });
        },
        (error) => {
          console.error("Error fetching post:", error);
          setLoading(false);
        }
      );
    }
  }, [slug]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root || !post?.post) return;

    const images = Array.from(root.querySelectorAll("img"));
    const onError = (event: Event) => {
      hideBrokenImage(event.currentTarget as HTMLImageElement);
    };

    images.forEach((img) => {
      if (img.complete && img.naturalWidth === 0 && img.getAttribute("src")) {
        hideBrokenImage(img);
        return;
      }
      img.addEventListener("error", onError);
    });

    return () => {
      images.forEach((img) => img.removeEventListener("error", onError));
    };
  }, [post?.post]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!post) {
    return (
      <>
        <SeoHead
          title="Post not found | STR Monitoring"
          description="This blog post could not be found."
          canonical="/blog"
          skipHreflang
          robots="noindex, follow"
        />
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="h5">Post not found</Typography>
        </Box>
      </>
    );
  }

  const faqs = extractFaqsFromHtml(post.post);
  const localeRobots =
    locale === DEFAULT_LOCALE ? undefined : "noindex, follow";

  return (
    <>
      <SeoHead
        title={`${post.title} | Short-Term Rental Monitoring`}
        description={post.description}
        keywords={post.tags?.join(", ")}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogTitle={post.title}
        ogDescription={post.description}
        ogImage={post.coverUrl}
        publishedTime={new Date(post.createdAt).toISOString()}
        modifiedTime={new Date(post.createdAt).toISOString()}
        articleTags={post.tags}
        alternateRssHref="/rss.xml"
        skipHreflang
        robots={localeRobots}
        jsonLd={[
          organizationSchema(),
          blogPostingSchema({
            title: post.title,
            description: post.description,
            slug: post.slug,
            coverUrl: post.coverUrl,
            authorName: post.author?.name,
            datePublished: new Date(post.createdAt).toISOString(),
            articleHtml: post.post,
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${post.slug}` },
          ]),
          ...(faqs.length >= 2 ? [faqPageSchema(faqs)] : []),
        ]}
      />

      {/* Page Content */}
      <Box sx={{ maxWidth: "1200px", mx: "auto", my: 4, p: 2, width: "100%" }}>
        {/* Title */}
        <Typography
          component="h1"
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 2,
            color: "#fff",
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" },
            lineHeight: 1.2,
            overflowWrap: "anywhere",
          }}
        >
          {post.title || "Untitled"}
        </Typography>

        {/* Date and Duration */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: "bold", color: "#a78bfa" }}
        >
          {post.author.name || "Unknown Author"}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: "#94a3b8", mb: 4 }}>
          {post.createdAt
            ? new Date(post.createdAt).toLocaleDateString()
            : "Date unknown"}
          {post.duration && ` • ${post.duration}`}
        </Typography>

        {/* Cover Image — hide entirely if the URL fails to load */}
        {post.coverUrl?.trim() && !coverFailed && (
          <Box sx={{ mb: 4, mt: 4, display: "flex", justifyContent: "center" }}>
            <img
              src={post.coverUrl}
              alt={post.title || "Blog post cover"}
              onError={(e) => {
                setCoverFailed(true);
                hideBrokenImage(e.currentTarget);
              }}
              style={{
                width: "100%",
                maxHeight: "700px",
                maxWidth: "700px",
                borderRadius: "8px",
              }}
            />
          </Box>
        )}

        {/* Tags */}
        <Box sx={{ mb: 4 }}>
          {post.tags?.length ? (
            post.tags.map((tag, index) => (
              <Chip key={index} label={tag} sx={{ mr: 1, mb: 1 }} />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No tags available
            </Typography>
          )}
        </Box>

        {/* Post Content */}
        <Box
          ref={contentRef}
          sx={{
            mb: 4,
            textAlign: "start",
            color: "#cbd5e1",
            overflowWrap: "anywhere",
            "& p, & li, & span, & div": { color: "#cbd5e1" },
            "& h1, & h2, & h3, & h4, & h5, & h6, & strong": { color: "#fff" },
            "& a": { color: "#a78bfa" },
            "& img": {
              maxWidth: "100%",
              height: "auto",
              display: "block",
            },
            "& table": {
              display: "block",
              maxWidth: "100%",
              overflowX: "auto",
            },
            "& pre": {
              overflowX: "auto",
              maxWidth: "100%",
            },
          }}
        >
          {post.post ? (
            <Typography
              variant="body1"
              component="div"
              dangerouslySetInnerHTML={{ __html: post.post }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No content available for this post.
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default BlogPost;
